import React, { useState, useEffect, useRef } from "react";
import {
  Alert,
  Card,
  Typography,
  Button,
  Input,
  Space,
  Row,
  Col,
  message,
  Statistic,
  Divider,
  Empty,
  List,
  Skeleton,
  InputRef,
} from "antd";
import "remixicon/fonts/remixicon.css";
import { useGlobal } from "../contexts/GlobalContext";
import request from "../utils/request";
import { payment } from "../utils/payment";

const { Title, Paragraph, Text } = Typography;

interface InviteInfo {
  invite_code: string;
  invite_count: number;
  reward_total: number;
}

interface RewardProduct {
  id: number;
  code: string;
  type: string;
  name: string;
  description?: string;
  vip_level?: number;
}

const Invitation: React.FC = () => {
  const { API_BASE_URL, token } = useGlobal();
  const [loading, setLoading] = useState(false);
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [rewardProducts, setRewardProducts] = useState<RewardProduct[]>([]);
  const [loadingRewards, setLoadingRewards] = useState(false);
  const inputRef = useRef<InputRef>(null);

  const inviteLink = `https://ai.zhheo.com/console/login?InviteID=${
    inviteInfo?.invite_code || ""
  }`;

  const fetchInviteInfo = React.useCallback(async () => {
    if (!token || !API_BASE_URL) return;

    setLoading(true);
    try {
      const response = await request.get(`${API_BASE_URL}/api/v3/invite`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        setInviteInfo(response.data.data);
      } else {
        message.error(response.data.msg || "获取邀请信息失败");
      }
    } catch (error) {
      message.error("获取邀请信息失败");
    } finally {
      setLoading(false);
    }
  }, [token, API_BASE_URL]);

  const fetchRewardProducts = React.useCallback(async () => {
    if (!token) return;

    setLoadingRewards(true);
    try {
      const products = await payment.getRewardProducts(token);
      setRewardProducts(products || []);
    } catch (error) {
      console.error("获取返利奖励产品失败:", error);
    } finally {
      setLoadingRewards(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchInviteInfo();
      fetchRewardProducts();
    }
  }, [token, fetchInviteInfo, fetchRewardProducts]);

  const handleCopy = () => {
    if (inputRef.current) {
      const input = inputRef.current.input;
      if (input) {
        input.select();
      }
    }

    navigator.clipboard
      .writeText(inviteLink)
      .then(() => {
        message.success({
          content: "邀请链接已复制到剪贴板",
          icon: <i className="ri-check-line" style={{ color: "#52c41a" }} />,
        });
      })
      .catch(() => {
        message.error("复制失败，请手动复制");
      });
  };

  return (
    <div
      style={{
        background: "#f0f2f5",
        minHeight: "100%",
        borderRadius: 8,
        padding: "16px",
      }}
    >
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card
            title={
              <div style={{ display: "flex", alignItems: "center" }}>
                <i
                  className="ri-user-star-line"
                  style={{
                    fontSize: "20px",
                    color: "#1890ff",
                    marginRight: "8px",
                  }}
                />
                <span>我的邀请</span>
              </div>
            }
            variant="borderless"
            style={{
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <Row gutter={[24, 24]}>
              <Col xs={24} md={16}>
                <Space
                  direction="vertical"
                  size="large"
                  style={{ width: "100%" }}
                >
                  <div>
                    <Title
                      level={4}
                      style={{ fontSize: "22px", marginBottom: "16px" }}
                    >
                      <i
                        className="ri-gift-line"
                        style={{ color: "#faad14", marginRight: "8px" }}
                      />
                      邀请好友开通相关产品后，你与好友各得会员时长
                    </Title>

                    <div
                      style={{
                        background:
                          "linear-gradient(135deg, #1677ff 0%, #69c0ff 100%)",
                        padding: "24px",
                        borderRadius: "12px",
                        color: "white",
                        marginBottom: "16px",
                        boxShadow: "0 8px 16px rgba(24, 144, 255, 0.2)",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {/* 背景装饰图标 */}
                      <div
                        style={{
                          position: "absolute",
                          top: "10px",
                          right: "10px",
                          opacity: 0.1,
                          fontSize: "80px",
                        }}
                      >
                        <i className="ri-share-circle-line" />
                      </div>

                      {/* 标题 */}
                      <div
                        style={{
                          fontSize: "18px",
                          marginBottom: "16px",
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <i
                          className="ri-link"
                          style={{ marginRight: "10px", fontSize: "22px" }}
                        />
                        您的专属邀请链接
                      </div>

                      {/* 输入框与复制按钮 */}
                      <div
                        className="invite-link-container"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <div style={{ flex: 1, position: "relative" }}>
                          <Input
                            ref={inputRef}
                            value={inviteLink}
                            readOnly
                            size="large"
                            style={{
                              backgroundColor: "rgba(255, 255, 255, 0.15)",
                              color: "white",
                              borderColor: "rgba(255, 255, 255, 0.3)",
                              borderRadius: "8px",
                              fontWeight: "500",
                              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                              width: "100%",
                            }}
                            prefix={
                              <i
                                className="ri-external-link-line"
                                style={{ color: "rgba(255, 255, 255, 0.6)" }}
                              />
                            }
                          />
                        </div>
                        <Button
                          type="primary"
                          onClick={handleCopy}
                          size="large"
                          style={{
                            marginLeft: "8px",
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                            borderColor: "transparent",
                            color: "#1677ff",
                            fontWeight: "bold",
                            borderRadius: "8px",
                            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            height: "40px",
                          }}
                        >
                          <i className="ri-file-copy-line" />
                          复制链接
                        </Button>
                      </div>

                      {/* 底部提示 */}
                      <div
                        style={{
                          marginTop: "16px",
                          fontSize: "13px",
                          opacity: "0.8",
                        }}
                      >
                        <i
                          className="ri-information-line"
                          style={{ marginRight: "6px" }}
                        />
                        复制后分享给好友，好友通过链接注册并消费，双方均可获得奖励
                      </div>
                    </div>

                    <Card
                      title="邀请奖励规则"
                      size="small"
                      style={{ marginBottom: "16px" }}
                      styles={{
                        header: { backgroundColor: "#f5f5f5" }
                      }}
                    >
                      <Paragraph style={{ marginBottom: "8px" }}>
                        <i
                          className="ri-information-line"
                          style={{ color: "#1890ff", marginRight: "8px" }}
                        />
                        被邀请用户从未在 HongMoAI
                        有过消费，使用邀请链接登录即可绑定。被邀请用户在注册
                        <Text strong>一年内</Text>
                        消费对应产品后，您与被邀请人均可获得奖励。
                      </Paragraph>
                      <Paragraph>
                        <i
                          className="ri-information-line"
                          style={{ color: "#1890ff", marginRight: "8px" }}
                        />
                        详情请参考右侧 <Text strong>返利奖励一览</Text>
                      </Paragraph>
                    </Card>
                  </div>
                </Space>
              </Col>

              <Col xs={24} md={8}>
                <Card
                  style={{
                    backgroundColor: "#fffbe6",
                    borderColor: "#ffe58f",
                    height: "100%",
                  }}
                  styles={{
                    body: { padding: "16px" }
                  }}
                >
                  <Statistic
                    title={
                      <div
                        style={{
                          color: "#d48806",
                          fontWeight: "bold",
                          fontSize: "16px",
                        }}
                      >
                        <i
                          className="ri-user-received-2-line"
                          style={{ marginRight: "8px" }}
                        />
                        已邀请人数
                      </div>
                    }
                    value={inviteInfo?.invite_count || 0}
                    valueStyle={{
                      color: "#d48806",
                      fontWeight: "bold",
                      fontSize: "36px",
                    }}
                    prefix={
                      <i
                        className="ri-user-follow-line"
                        style={{ fontSize: "24px" }}
                      />
                    }
                  />

                  <Divider style={{ margin: "16px 0" }} />

                  <Statistic
                    title={
                      <div
                        style={{
                          color: "#d48806",
                          fontWeight: "bold",
                          fontSize: "16px",
                        }}
                      >
                        <i
                          className="ri-award-line"
                          style={{ marginRight: "8px" }}
                        />
                        累计返利
                      </div>
                    }
                    value={inviteInfo?.reward_total || 0}
                    valueStyle={{
                      color: "#d48806",
                      fontWeight: "bold",
                      fontSize: "32px",
                    }}
                    suffix="份"
                  />

                  <Divider style={{ margin: "16px 0" }} />

                  <div style={{ marginBottom: "16px" }}>
                    <div
                      style={{
                        fontWeight: "bold",
                        color: "#d48806",
                        marginBottom: "8px",
                      }}
                    >
                      <i
                        className="ri-award-line"
                        style={{ marginRight: "8px" }}
                      />
                      返利奖励一览
                    </div>

                    {loadingRewards ? (
                      <Skeleton active paragraph={{ rows: 2 }} />
                    ) : rewardProducts.length > 0 ? (
                      <List
                        size="small"
                        dataSource={rewardProducts}
                        renderItem={(item) => (
                          <List.Item style={{ padding: "8px 0" }}>
                            <Space>
                              <i
                                className="ri-gift-2-line"
                                style={{ color: "#faad14" }}
                              />
                              <span>{item.name}</span>
                            </Space>
                            {item.description && (
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#8c8c8c",
                                  marginTop: "4px",
                                }}
                              >
                                {item.description}
                              </div>
                            )}
                          </List.Item>
                        )}
                      />
                    ) : (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="暂无奖励产品"
                      />
                    )}
                  </div>
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col span={24}>
          <Card
            title={
              <div style={{ display: "flex", alignItems: "center" }}>
                <i
                  className="ri-history-line"
                  style={{
                    fontSize: "20px",
                    color: "#1890ff",
                    marginRight: "8px",
                  }}
                />
                <span>邀请汇总</span>
              </div>
            }
            variant="borderless"
            style={{
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
            styles={{
              body: { padding: 0 }
            }}
          >
            <div style={{ padding: "16px" }}>
              <Alert
                type="info"
                showIcon
                message="当前版本展示邀请汇总"
                description="v3 邀请接口当前提供邀请码、累计邀请人数和累计返利，不再返回逐条邀请明细。您仍可继续分享邀请码并查看奖励产品规则。"
                style={{ marginBottom: "16px" }}
              />

              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Card size="small" styles={{ body: { padding: "20px" } }}>
                    <Statistic
                      title="累计邀请人数"
                      value={inviteInfo?.invite_count || 0}
                      prefix={<i className="ri-team-line" />}
                    />
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card size="small" styles={{ body: { padding: "20px" } }}>
                    <Statistic
                      title="累计返利"
                      value={inviteInfo?.reward_total || 0}
                      prefix={<i className="ri-gift-line" />}
                      suffix="份"
                    />
                  </Card>
                </Col>
              </Row>

              {!loading && !inviteInfo?.invite_count ? (
                <Empty
                  style={{ marginTop: "24px" }}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span>
                      暂无邀请记录，快去
                      <Button
                        type="link"
                        onClick={handleCopy}
                        style={{ padding: "0 4px" }}
                      >
                        分享您的邀请链接
                      </Button>
                      吧！
                    </span>
                  }
                />
              ) : null}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Invitation;
